import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Modal, Button, Select, Input, InputNumber, Tag, Spinner, EmptyState, message, Tooltip } from '../../ui';
import { Plus, Trash2, Info, Send, Bike, Car, Bus, Plane, TrainFront, CarTaxiFront, Route } from 'lucide-react';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { FileUploadField } from '../../components/FileUploadField';
import { AttachmentCell } from '../../components/AttachmentPreview';
import { inr2 } from '../../lib/format';

const OTHER_CATEGORIES = ['FOOD', 'HOTEL', 'LOCAL_CONVEYANCE', 'TOLL_PARKING', 'OTHER'];
const DESC_REQUIRED = new Set(['HOTEL', 'OTHER']);

/* The words a rep reads for a stored category code. The CODE is what is saved and what the server's
   proof rules key on; only the display changes. "TOLL PARKING" and "LOCAL CONVEYANCE" were the raw codes. */
const CATEGORY_LABEL: Record<string, string> = {
  FOOD: 'Food',
  HOTEL: 'Hotel',
  LOCAL_CONVEYANCE: 'Local conveyance',
  TOLL_PARKING: 'Toll & parking',
  OTHER: 'Other',
};
/** A code outside the list (a travel mode, the allowance) reads in sentence case rather than capitals. */
const categoryLabel = (code?: string | null): string => {
  const c = String(code ?? '');
  if (CATEGORY_LABEL[c]) return CATEGORY_LABEL[c];
  const words = c.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

type Draft = {
  key: string;
  kind: 'TRAVEL' | 'OTHER';
  travelMode?: string;
  distanceKm?: number;
  category?: string;
  amount?: number;
  description?: string;
  visitId?: string;
  billUrl?: string;
};
const blank = (kind: Draft['kind']): Draft => ({ key: Math.random().toString(36).slice(2), kind });

/**
 * The rep's expense claim for ONE FIELD DAY.
 *
 * A day's costs belong to the day, not to a single visit — the day's fuel can't be split across its
 * farmers, and the daily allowance is decided by the whole day's distance. So this is where a rep
 * builds up the day: travel by the kilometre (priced by the server from the rate card), anything
 * billed at actuals, each line optionally tagged to one of that day's visits.
 *
 * The allowance is NOT entered here. It is worked out on submit, once the day's distance is final —
 * what's shown before then is a preview of what the current distance would earn.
 */
/**
 * A FACE PER TRAVEL MODE.
 *
 * Seven near-identical lines of text ("Bus — at actuals", "Train — at actuals" …) are read one word
 * at a time; a shape is recognised before it is read, which is what a rep picking a mode at the end of
 * a long day actually needs. The colours are the transport's own — they carry no status meaning, so
 * they are kept muted enough not to compete with the amber/red the rest of this form uses for money
 * and for errors.
 */
const TRAVEL_ICON: Record<string, { icon: JSX.Element; tint: string }> = {
  BIKE: { icon: <Bike className="size-4" />, tint: 'text-emerald-600 dark:text-emerald-400' },
  CAR: { icon: <Car className="size-4" />, tint: 'text-blue-600 dark:text-blue-400' },
  BUS: { icon: <Bus className="size-4" />, tint: 'text-amber-600 dark:text-amber-400' },
  FLIGHT: { icon: <Plane className="size-4" />, tint: 'text-sky-600 dark:text-sky-400' },
  TRAIN: { icon: <TrainFront className="size-4" />, tint: 'text-violet-600 dark:text-violet-400' },
  TAXI: { icon: <CarTaxiFront className="size-4" />, tint: 'text-yellow-600 dark:text-yellow-500' },
  OTHER: { icon: <Route className="size-4" />, tint: 'text-muted' },
};

export function DayExpenseModal({ date, visits = [], onClose, onSaved }: {
  date: string;
  /** That day's visits, offered as an optional tag on a line. */
  visits?: Array<{ id: string | number; label: string }>;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState<Draft[]>([blank('TRAVEL')]);

  const { data: claim, isLoading } = useQuery({
    queryKey: ['day-claim', date],
    queryFn: () => request<any>({ url: `/expense-reports/day/${date}` }),
  });
  const { data: policy } = useQuery({
    queryKey: ['expense-policy', { on: date }],
    queryFn: () => request<any>({ url: '/expense-policy', params: { on: date } }),
  });

  const report = claim?.report ?? null;
  const locked = !!report && !['DRAFT', 'CORRECTION_REQUIRED'].includes(report.status);
  const rates: any[] = policy?.travelRates ?? [];
  const rateOf = (code?: string) => rates.find((r) => r.code === code);
  /** Proof for a non-travel cost: the policy row when the category is also a travel code (OTHER, TAXI…),
   *  otherwise a receipt — mirrors the server's proofRules, which refuses to submit without it. */
  const otherProof = (category?: string) => {
    const r = rateOf(category);
    return r ? { required: !!r.proofRequired, label: String(r.proofLabel ?? 'Proof') } : { required: true, label: 'Receipt' };
  };

  const patch = (key: string, p: Partial<Draft>) => setDrafts((a) => a.map((d) => (d.key === key ? { ...d, ...p } : d)));
  const drop = (key: string) => setDrafts((a) => a.filter((d) => d.key !== key));

  // What the lines already saved on the claim add up to, and how far the day has travelled so far.
  const saved = useMemo(() => {
    const items: any[] = report?.items ?? [];
    return {
      total: items.reduce((s, i) => s + Number(i.amount || 0), 0),
      km: items.reduce((s, i) => s + Number(i.distanceKm || 0), 0),
      items,
    };
  }, [report]);

  // Distance and value the drafts would add, so the rep sees where the day lands before saving.
  const pending = useMemo(() => {
    let total = 0; let km = 0;
    for (const d of drafts) {
      if (d.kind === 'TRAVEL') {
        const r = rateOf(d.travelMode);
        if (r?.basis === 'PER_KM' && d.distanceKm) { km += Number(d.distanceKm); total += Number(d.distanceKm) * Number(r.ratePerKm); }
        else if (r?.basis === 'ACTUAL' && d.amount) total += Number(d.amount);
      } else if (d.amount) total += Number(d.amount);
    }
    return { total, km };
  }, [drafts, rates]);

  const dayKm = saved.km + pending.km;
  // Preview only — the server recomputes this at submit from the day's final distance.
  const daPreview = useMemo(() => {
    const slab = (policy?.daSlabs ?? []).find((s: any) => dayKm >= s.minKm);
    return slab ? Number(slab.amount) : 0;
  }, [policy, dayKm]);

  const save = useConfirmedMutation({ title: "Save this day’s expenses?", content: "Saved as a draft — you still have to submit the claim separately.", okText: "Yes, save" }, {
    mutationFn: () => {
      /*
       * An UNTOUCHED row is the empty form, not an error.
       *
       * The sheet always opens with a blank TRAVEL row. A rep who clicks "Other cost", fills a hotel
       * bill and saves was told "Pick how you travelled" — about the blank row they never chose to add
       * — and no request was sent. Nothing on screen said which row was at fault, and the only way out
       * was to notice the little bin icon on the row above. So blank rows are dropped here, and the
       * validation below applies to rows the rep actually filled in.
       */
      const touched = drafts.filter((d) => d.travelMode || d.category || d.amount != null || d.distanceKm != null
        || (d.description ?? '').trim() || d.billUrl || d.visitId);
      if (!touched.length) throw new Error('Add a travel or a cost entry first');
      const lines = touched.map((d) => {
        if (d.kind === 'TRAVEL') {
          const r = rateOf(d.travelMode);
          if (!r) throw new Error('Pick how you travelled');
          if (r.basis === 'PER_KM') {
            if (!d.distanceKm || d.distanceKm <= 0) throw new Error(`Enter the kilometres travelled by ${r.label}`);
            // Proof is enforced at submit and approval too (server) — the policy decides which modes need it.
            if (r.proofRequired && !d.billUrl) throw new Error(`${r.label} needs ${r.proofLabel ?? 'proof'} attached`);
            return { kind: 'TRAVEL_KM', travelMode: r.code, distanceKm: Number(d.distanceKm), visitId: d.visitId, billUrl: d.billUrl, description: d.description };
          }
          if (!d.amount || d.amount <= 0) throw new Error(`Enter what you spent on ${r.label}`);
          if (r.proofRequired && !d.billUrl) throw new Error(`${r.label} needs ${r.proofLabel ?? 'proof'} attached`);
          return { kind: 'ACTUAL', category: r.code, amount: Number(d.amount), visitId: d.visitId, billUrl: d.billUrl, description: d.description };
        }
        if (!d.category) throw new Error('Pick what the expense was for');
        if (!d.amount || d.amount <= 0) throw new Error('Every expense needs an amount');
        if (DESC_REQUIRED.has(d.category) && !d.description?.trim()) throw new Error(`${categoryLabel(d.category)} expenses need a description`);
        // Every cost the rep paid needs its receipt — the same rule the server applies at submit.
        if (otherProof(d.category).required && !d.billUrl) throw new Error(`${categoryLabel(d.category)} needs a ${otherProof(d.category).label.toLowerCase()} attached`);
        return { kind: 'ACTUAL', category: d.category, amount: Number(d.amount), description: d.description, visitId: d.visitId, billUrl: d.billUrl };
      });
      return request({ url: '/expense-reports/day', method: 'POST', data: { expenseDate: date, lines } });
    },
    onSuccess: () => {
      message.success('Added to the day’s claim');
      setDrafts([blank('TRAVEL')]);
      qc.invalidateQueries({ queryKey: ['day-claim', date] });
      qc.invalidateQueries({ queryKey: ['expense-reports'] });
      qc.invalidateQueries({ queryKey: ['my-day'] });
      onSaved?.();
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? e?.message ?? 'Could not save'),
  });

  /* PROOF ON A LINE ALREADY SAVED. A claim sent back for proof came back to the rep with its lines
     intact and nowhere to attach the missing bill — the sheet listed them as text only, so the one
     thing the approver asked for could not be done. Uploading here patches that expense's bill. */
  const attachProof = useMutation({
    mutationFn: ({ itemId, billUrl }: { itemId: string; billUrl: string }) =>
      request({ url: `/expense-reports/${report.id}/items/${itemId}`, method: 'PATCH', data: { billUrl } }),
    onSuccess: () => {
      message.success('Proof attached');
      qc.invalidateQueries({ queryKey: ['day-claim', date] });
      qc.invalidateQueries({ queryKey: ['expense-report', String(report?.id)] });
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not attach the proof'),
  });
  // Saved lines still missing required proof — the server refuses to submit while any remain.
  const missingProof = (saved.items as any[]).filter((i) => i.proofRequired && !i.billUrl);

  const submit = useConfirmedMutation({ title: "Submit this expense claim?", content: "It goes to your manager for approval and you can no longer edit it.", okText: "Yes, submit" }, {
    mutationFn: () => request({ url: `/expense-reports/${report.id}/submit`, method: 'PATCH' }),
    onSuccess: () => {
      message.success('Claim submitted for approval');
      qc.invalidateQueries({ queryKey: ['day-claim', date] });
      qc.invalidateQueries({ queryKey: ['expense-reports'] });
      onSaved?.();
      onClose();
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not submit'),
  });

  return (
    <Modal
      open onOpenChange={(o) => { if (!o) onClose(); }}
      title={`Expenses for ${dayjs(date).format('ddd, DD MMM YYYY')}`}
      description={report ? `${report.reportNo} · ${String(report.status).replace(/_/g, ' ')}` : 'No claim opened for this day yet'}
      width={860}
      footer={(
        <>
          <span className="mr-auto text-sm text-muted">
            Day so far <span className="font-semibold text-content">{inr2(saved.total + pending.total)}</span>
            {dayKm > 0 && <> · {dayKm} km</>}
          </span>
          <Button variant="default" onClick={onClose}>Close</Button>
          {/* Hidden once the day's claim has left DRAFT — one claim per day, and the server refuses
              a second one. Offering the button would only produce an error. */}
          {!locked && (
            <Button variant="default" loading={save.isPending} disabled={!drafts.length} onClick={() => save.mutate()}>
              Add to claim
            </Button>
          )}
          {report && !locked && (
            <Tooltip label={missingProof.length ? 'Attach the missing proof first' : 'Send the whole day to your approver'}>
              <Button variant="primary" icon={<Send size={16} />} loading={submit.isPending} disabled={missingProof.length > 0} onClick={() => submit.mutate()}>
                Submit day
              </Button>
            </Tooltip>
          )}
        </>
      )}
    >
      {isLoading ? <div className="py-10 text-center"><Spinner /></div> : (
        <div className="space-y-5">
          {locked && (
            <div className="rounded-card border border-warning-500/30 bg-warning-500/5 px-3 py-2.5 text-sm text-content">
              {report.reportNo} for this day is already <b>{String(report.status).replace(/_/g, ' ').toLowerCase()}</b>,
              so it can’t take new expenses. There is only ever <b>one claim per day</b>. If you still need to add
              something to this day, ask your approver to send the claim back — it reopens for editing.
            </div>
          )}

          {/* Sent back: say why, right where the rep can act on it. */}
          {report?.status === 'CORRECTION_REQUIRED' && report.correctionReason && (
            <div className="rounded-card border border-warning-500/40 bg-warning-500/10 px-3 py-2.5 text-sm text-warning-600 dark:text-harvest-300">
              <b>Sent back by your approver:</b> {report.correctionReason}
            </div>
          )}
          {!locked && missingProof.length > 0 && (
            <div className="rounded-card border border-danger-500/40 bg-danger-500/10 px-3 py-2.5 text-sm text-danger-600 dark:text-danger-500">
              {missingProof.length} {missingProof.length === 1 ? 'expense needs' : 'expenses need'} proof before this day can be submitted — attach it below.
            </div>
          )}

          {/* ── What the day already holds ── */}
          {saved.items.length > 0 && (
            <section>
              <h3 className="mb-2 border-b border-line pb-2 type-body-strong text-content">Already on this day</h3>
              <div className="divide-y divide-line/70">
                {saved.items.map((i: any) => (
                  <div key={i.id} className="flex items-center gap-3 py-2 text-sm">
                    <span className="min-w-0 flex-1">
                      <span className="font-medium text-content">{categoryLabel(i.category)}</span>
                      {i.travelMode && <span className="text-muted"> · {i.travelMode}</span>}
                      {i.distanceKm > 0 && <span className="text-muted"> · {Number(i.distanceKm)} km @ {inr2(i.ratePerKm)}/km</span>}
                      {i.description && <span className="block truncate text-xs text-subtle">{i.description}</span>}
                    </span>
                    {/* isSystem is a 0/1 tinyint — coerce so a non-system line (0) doesn't render a stray "0". */}
                    {!!i.isSystem && <Tag tone="blue">Worked out for you</Tag>}
                    {/* The proof: a small preview, or — while the claim is still editable — the place to add it. */}
                    <span className="flex shrink-0 items-center gap-2">
                      {i.billUrl && <AttachmentCell files={[i.billUrl]} label={`${i.proofLabel ?? 'Proof'} · ${categoryLabel(i.category).toLowerCase()}`} size={32} />}
                      {!i.billUrl && i.proofRequired && (
                        <span className="rounded-full border border-danger-500/40 bg-danger-500/10 px-2 py-0.5 type-caption font-medium text-danger-600 dark:text-danger-500">
                          {i.proofLabel ?? 'Proof'} missing
                        </span>
                      )}
                      {!locked && !i.isSystem && (
                        <FileUploadField size="small" category="receipt" preview={false} compact
                          buttonLabel={i.billUrl ? 'Replace' : `Attach ${String(i.proofLabel ?? 'proof').toLowerCase()}`}
                          value={i.billUrl} onChange={(url) => { if (url) attachProof.mutate({ itemId: String(i.id), billUrl: url }); }} />
                      )}
                    </span>
                    <span className="shrink-0 font-semibold tabular-nums text-content">{inr2(i.amount)}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ── New lines ── */}
          {!locked && (
            <section>
              <div className="mb-2 flex flex-wrap items-center gap-2 border-b border-line pb-2">
                <h3 className="type-body-strong text-content">Add to the day</h3>
                <div className="ml-auto flex flex-wrap gap-2">
                  <Button size="sm" variant="default" icon={<Plus size={14} />} onClick={() => setDrafts((a) => [...a, blank('TRAVEL')])}>Travel</Button>
                  <Button size="sm" variant="default" icon={<Plus size={14} />} onClick={() => setDrafts((a) => [...a, blank('OTHER')])}>Other cost</Button>
                </div>
              </div>

              {drafts.length === 0 ? <EmptyState title="Nothing to add yet" /> : (
                <div className="space-y-3">
                  {drafts.map((d) => {
                    const r = d.kind === 'TRAVEL' ? rateOf(d.travelMode) : undefined;
                    const perKm = r?.basis === 'PER_KM';
                    return (
                      <div key={d.key} className="rounded-card border border-line bg-surface-2 p-3">
                        <div className="flex flex-wrap items-end gap-3">
                          {d.kind === 'TRAVEL' ? (
                            <>
                              <label className="min-w-40 flex-1">
                                <span className="mb-1 block text-xs text-muted">How you travelled</span>
                                <Select
                                  className="w-full" placeholder="Travel mode" value={d.travelMode}
                                  onChange={(v) => patch(d.key, { travelMode: v, distanceKm: undefined, amount: undefined })}
                                  options={rates.map((x) => {
                                    const m = TRAVEL_ICON[String(x.code).toUpperCase()] ?? TRAVEL_ICON.OTHER;
                                    const how = x.basis === 'PER_KM' ? `${inr2(x.ratePerKm)}/km` : 'at actuals';
                                    return {
                                      value: x.code,
                                      /* Plain text for type-to-search; the rendered label carries the icon. */
                                      title: `${x.label} — ${how}`,
                                      label: (
                                        <span className="flex min-w-0 items-center gap-2">
                                          <span className={`shrink-0 ${m.tint}`}>{m.icon}</span>
                                          <span className="truncate text-content">{x.label}</span>
                                          <span className="ml-auto shrink-0 type-caption text-subtle">{how}</span>
                                        </span>
                                      ),
                                    };
                                  })}
                                />
                              </label>
                              {perKm ? (
                                <label className="w-40">
                                  <span className="mb-1 block text-xs text-muted">Distance (km)</span>
                                  <InputNumber min={0.1} className="w-full" placeholder="0"
                                    value={d.distanceKm} onChange={(v) => patch(d.key, { distanceKm: v == null ? undefined : Number(v) })} />
                                </label>
                              ) : d.travelMode ? (
                                <label className="w-40">
                                  <span className="mb-1 block text-xs text-muted">What you spent</span>
                                  <InputNumber min={1} prefix="₹" className="w-full" placeholder="0.00"
                                    value={d.amount} onChange={(v) => patch(d.key, { amount: v == null ? undefined : Number(v) })} />
                                </label>
                              ) : null}
                              {perKm && d.distanceKm ? (
                                <div className="pb-1.5 text-sm">
                                  <span className="text-muted">comes to </span>
                                  <span className="font-semibold text-content">{inr2(Number(d.distanceKm) * Number(r!.ratePerKm))}</span>
                                </div>
                              ) : null}
                            </>
                          ) : (
                            <>
                              <label className="min-w-40 flex-1">
                                <span className="mb-1 block text-xs text-muted">What for</span>
                                <Select className="w-full" placeholder="Category" value={d.category}
                                  onChange={(v) => patch(d.key, { category: v })}
                                  options={OTHER_CATEGORIES.map((c) => ({ value: c, label: categoryLabel(c) }))} />
                              </label>
                              <label className="w-40">
                                <span className="mb-1 block text-xs text-muted">Amount</span>
                                <InputNumber min={1} prefix="₹" className="w-full" placeholder="0.00"
                                  value={d.amount} onChange={(v) => patch(d.key, { amount: v == null ? undefined : Number(v) })} />
                              </label>
                            </>
                          )}
                          <Button variant="text" size="sm" aria-label="Remove expense" className="mb-0.5 text-muted hover:bg-danger-500/10 hover:text-danger-600"
                            icon={<Trash2 size={16} />} onClick={() => drop(d.key)} />
                        </div>

                        <div className="mt-3 flex flex-wrap items-end gap-3">
                          <label className="min-w-48 flex-1">
                            <span className="mb-1 block text-xs text-muted">
                              Note {d.kind === 'OTHER' && d.category && DESC_REQUIRED.has(d.category) ? '(required)' : '(optional)'}
                            </span>
                            <Input placeholder="What was this for?" value={d.description ?? ''} onChange={(e) => patch(d.key, { description: e.target.value })} />
                          </label>
                          {visits.length > 0 && (
                            <label className="min-w-48 flex-1">
                              <span className="mb-1 block text-xs text-muted">For a particular visit (optional)</span>
                              <Select className="w-full" allowClear placeholder="A cost for the whole day"
                                value={d.visitId} onChange={(v) => patch(d.key, { visitId: v })}
                                options={visits.map((v) => ({ value: String(v.id), label: v.label }))} />
                            </label>
                          )}
                          <div>
                            <span className="mb-1 block text-xs text-muted">
                              {(() => {
                                const need = d.kind === 'TRAVEL'
                                  ? { required: !!r?.proofRequired, label: r?.proofLabel ?? 'Proof' }
                                  : otherProof(d.category);
                                return need.required ? `${need.label} (required)` : 'Proof (optional)';
                              })()}
                            </span>
                            <FileUploadField size="small" category="receipt" preview={false}
                              buttonLabel={d.billUrl ? 'Replace' : 'Attach'} value={d.billUrl} onChange={(url) => patch(d.key, { billUrl: url })} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* ── The allowance, and why it is what it is ── */}
          <div className="flex flex-wrap items-center gap-2 rounded-card border border-line bg-surface-2 px-3 py-2.5 text-sm">
            <Info size={16} className="shrink-0 text-muted" />
            {daPreview > 0 ? (
              <span className="text-content">
                At <b>{dayKm} km</b> this day earns a daily allowance of <b>{inr2(daPreview)}</b>.
              </span>
            ) : (
              <span className="text-muted">
                {dayKm > 0
                  ? `${dayKm} km does not reach the distance a daily allowance starts at.`
                  : 'Add your travel and the daily allowance will be worked out from the day’s distance.'}
              </span>
            )}
            <span className="text-subtle">Added automatically when you submit — you don’t enter it.</span>
          </div>
        </div>
      )}
    </Modal>
  );
}
